import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';

import { ApiError } from './api';
import { NativeBookingAccount } from './native-booking-account';
import { NativeBookingMyPage } from './native-booking-my';
import { NativeBookingPage } from './native-booking-page';
import { createNativeBookingApi, type NativeMe } from './native-booking-api';
import { NativeCabinetShell } from './native-cabinet-shell';
import { nativeProblemMessage } from './native-booking-problem';
import { navigate } from './path-router';

function isUnauthorized(failure: unknown): boolean {
  return failure instanceof ApiError && failure.status === 401;
}

export function NativeBookingArea(props: {
  readonly apiBaseUrl: string;
  readonly path: string;
}): React.ReactElement {
  const api = useMemo(() => createNativeBookingApi(props.apiBaseUrl), [props.apiBaseUrl]);
  const [bootstrapping, setBootstrapping] = useState(true);
  const [me, setMe] = useState<NativeMe | null>(null);
  const [sessionError, setSessionError] = useState<string | null>(null);
  const [catalogSites, setCatalogSites] = useState<readonly { id: string; display_name: string }[]>(
    [],
  );
  const [catalogEquipment, setCatalogEquipment] = useState<
    readonly { id: string; display_name: string }[]
  >([]);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [catalogError, setCatalogError] = useState<string | null>(null);
  const [logoutError, setLogoutError] = useState<string | null>(null);
  const [logoutBusy, setLogoutBusy] = useState(false);
  /** Monotonic generation — stale fetch results must not overwrite newer login/logout. */
  const generationRef = useRef(0);

  const clearCatalog = () => {
    setCatalogSites([]);
    setCatalogEquipment([]);
    setCatalogError(null);
    setCatalogLoading(false);
  };

  const loadCatalog = async (generation: number) => {
    setCatalogLoading(true);
    setCatalogError(null);
    try {
      const catalog = await api.catalog();
      if (generation !== generationRef.current) {
        return;
      }
      setCatalogSites(catalog.sites);
      setCatalogEquipment(catalog.equipment_types);
      setCatalogLoading(false);
    } catch (failure) {
      if (generation !== generationRef.current) {
        return;
      }
      setCatalogSites([]);
      setCatalogEquipment([]);
      setCatalogLoading(false);
      if (isUnauthorized(failure)) {
        setMe(null);
        setCatalogError(null);
        navigate('/account', 'replace');
        return;
      }
      setCatalogError(nativeProblemMessage(failure));
    }
  };

  const applySession = (next: NativeMe | null) => {
    const generation = ++generationRef.current;
    setLogoutError(null);
    setMe(next);
    if (next === null) {
      clearCatalog();
      return;
    }
    void loadCatalog(generation);
  };

  const probeSession = async () => {
    const generation = ++generationRef.current;
    setBootstrapping(true);
    setSessionError(null);
    setLogoutError(null);
    try {
      const session = await api.me();
      if (generation !== generationRef.current) {
        return;
      }
      setMe(session);
      setBootstrapping(false);
      await loadCatalog(generation);
    } catch (failure) {
      if (generation !== generationRef.current) {
        return;
      }
      if (isUnauthorized(failure)) {
        setMe(null);
        clearCatalog();
        setBootstrapping(false);
        return;
      }
      setMe(null);
      clearCatalog();
      setSessionError(nativeProblemMessage(failure));
      setBootstrapping(false);
    }
  };

  useEffect(() => {
    void probeSession();
    return () => {
      // Invalidate in-flight bootstrap/catalog for StrictMode remount / unmount.
      generationRef.current += 1;
    };
  }, [api]);

  useEffect(() => {
    if (bootstrapping || sessionError !== null) {
      return;
    }
    if (me === null && props.path !== '/account') {
      navigate('/account', 'replace');
    }
  }, [bootstrapping, me, props.path, sessionError]);

  const logout = async () => {
    setLogoutBusy(true);
    setLogoutError(null);
    try {
      await api.logout();
      applySession(null);
      navigate('/account', 'replace');
    } catch (failure) {
      // Do not claim local sign-out — HttpOnly session may still be active.
      setLogoutError(
        `${nativeProblemMessage(failure)} Сеанс на сервере мог остаться активным — повторите выход.`,
      );
    } finally {
      setLogoutBusy(false);
    }
  };

  if (bootstrapping) {
    return (
      <NativeCabinetShell path={props.path} me={me} onLogout={() => void logout()}>
        <p role="status" className="native-muted" data-testid="native-session-probe">
          Проверка сеанса…
        </p>
      </NativeCabinetShell>
    );
  }

  if (sessionError !== null) {
    return (
      <NativeCabinetShell path={props.path} me={me} onLogout={() => void logout()}>
        <p role="alert" className="native-banner native-banner-danger" data-testid="native-session-error">
          {sessionError}
        </p>
        <button
          type="button"
          className="native-btn native-btn-secondary"
          data-testid="native-session-retry"
          onClick={() => {
            void probeSession();
          }}
        >
          Повторить
        </button>
      </NativeCabinetShell>
    );
  }

  let content: ReactNode;
  if (props.path === '/account') {
    content = (
      <NativeBookingAccount
        api={api}
        me={me}
        onSession={(next) => {
          applySession(next);
        }}
      />
    );
  } else if (props.path === '/booking') {
    content = (
      <>
        {catalogError !== null ? (
          <div className="native-banner native-banner-danger" role="alert" data-testid="native-catalog-error">
            <p>Не удалось загрузить справочник площадок и техники: {catalogError}</p>
            <button
              type="button"
              className="native-link-btn"
              data-testid="native-catalog-retry"
              onClick={() => {
                void loadCatalog(++generationRef.current);
              }}
            >
              Повторить
            </button>
          </div>
        ) : null}
        {catalogLoading ? (
          <p role="status" className="native-muted" data-testid="native-catalog-loading">
            Загрузка справочника…
          </p>
        ) : null}
        <NativeBookingPage
          api={api}
          catalogSites={catalogSites}
          catalogEquipment={catalogEquipment}
        />
      </>
    );
  } else if (props.path === '/my') {
    content = (
      <>
        {catalogError !== null ? (
          <div className="native-banner native-banner-danger" role="alert" data-testid="native-catalog-error">
            <p>Не удалось загрузить справочник: {catalogError}</p>
            <button
              type="button"
              className="native-link-btn"
              data-testid="native-catalog-retry"
              onClick={() => {
                void loadCatalog(++generationRef.current);
              }}
            >
              Повторить
            </button>
          </div>
        ) : null}
        <NativeBookingMyPage
          api={api}
          catalogSites={catalogSites}
          catalogEquipment={catalogEquipment}
        />
      </>
    );
  } else {
    content = (
      <>
        <h1>Личный кабинет</h1>
        <p>Страница не найдена.</p>
      </>
    );
  }

  return (
    <NativeCabinetShell
      path={props.path}
      me={me}
      onLogout={() => {
        if (!logoutBusy) {
          void logout();
        }
      }}
    >
      {logoutError !== null ? (
        <p role="alert" className="native-banner native-banner-danger" data-testid="native-logout-error">
          {logoutError}
        </p>
      ) : null}
      {content}
    </NativeCabinetShell>
  );
}
